import React from 'react';
import { Card, Calendar, Row, Col, Badge, List, Tag, Button, Spin, Divider, Tooltip } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import moment from 'moment';
import '../../../../assets/scss/components/OfflineBooking/CalendarView.scss';

// Booking type constants from backend (BookingType.java enum)
const BOOKING_TYPES = {
  SYSTEM_BOOKING: 'SYSTEM_BOOKING',           // MillionSpaces - Online bookings
  MANUAL_BOOKING: 'MANUAL_BOOKING',           // Blocked by host - Manual reservations
  OFFLINE_BOOKING: 'OFFLINE_BOOKING',         // Offline Admin Booking
  GOOGLE_CALENDAR_EVENT: 'GOOGLE_CALENDAR_EVENT' // Google calendar synced events
};

// Platform display names
const PLATFORM_NAMES = {
  [BOOKING_TYPES.SYSTEM_BOOKING]: 'MillionSpaces',
  [BOOKING_TYPES.MANUAL_BOOKING]: 'Blocked by host',
  [BOOKING_TYPES.OFFLINE_BOOKING]: 'Offline Admin Booking',
  [BOOKING_TYPES.GOOGLE_CALENDAR_EVENT]: 'Google Calendar'
};

const CalendarView = ({ 
  selectedDate, 
  calendarData = {},  // Data from API in format: { "DD-MM-YYYY": [bookings] }
  loadingBookings,
  onCalendarSelect,
  onRefresh,
  onBookingClick
}) => {
  
  // Get bookings for a specific date from calendar data
  const getBookingsForDate = (value) => {
    if (!calendarData || typeof calendarData !== 'object') return [];
    const dateStr = value.format('DD-MM-YYYY');
    return calendarData[dateStr] || [];
  };

  // Render calendar cell with booking indicators
  const dateCellRender = (value) => {
    const bookings = getBookingsForDate(value);
    
    return (
      <ul className="calendar-events">
        {bookings.slice(0, 3).map((booking, index) => {
          const badgeStatus = getBookingBadgeStatus(booking.bookingType || booking.platformIdentity);
          return (
            <li key={booking.id || index}>
              <Badge 
                status={badgeStatus} 
                text={
                  <span className="event-time">
                    {booking.blocks && booking.blocks[0]?.startTime 
                      ? `${booking.blocks[0].startTime}-${booking.blocks[0].endTime}`
                      : 'All day'}
                  </span>
                }
              />
            </li>
          );
        })}
        {bookings.length > 3 && (
          <li className="more-events">+{bookings.length - 3} more</li>
        )}
      </ul>
    );
  };

  // Get badge status color based on booking type
  const getBookingBadgeStatus = (bookingType) => {
    switch (bookingType) {
      case BOOKING_TYPES.SYSTEM_BOOKING:
        return 'success';  // Green - Online bookings
      case BOOKING_TYPES.MANUAL_BOOKING:
        return 'processing';  // Blue - Manual reservations
      case BOOKING_TYPES.OFFLINE_BOOKING:
        return 'warning';  // Orange - Offline admin bookings
      case BOOKING_TYPES.GOOGLE_CALENDAR_EVENT:
        return 'default';  // Gray - Google calendar events
      default:
        return 'default';
    }
  };

  // Get tag color based on booking type
  const getBookingTagColor = (bookingType) => {
    switch (bookingType) {
      case BOOKING_TYPES.SYSTEM_BOOKING:
        return 'green';
      case BOOKING_TYPES.MANUAL_BOOKING:
        return 'blue';
      case BOOKING_TYPES.OFFLINE_BOOKING:
        return 'orange';
      case BOOKING_TYPES.GOOGLE_CALENDAR_EVENT:
        return 'purple';
      default:
        return 'default';
    }
  };

  // Get display name for booking platform
  const getPlatformName = (bookingType) => {
    return PLATFORM_NAMES[bookingType] || bookingType || 'Unknown';
  };

  // Get bookings for selected date
  const selectedDateBookings = getBookingsForDate(selectedDate);

  return (
    <div className="calendar-view"> 
      <Card 
        size="small" 
        className="calendar-card"
        extra={
          <Button 
            size="small" 
            onClick={onRefresh}
            loading={loadingBookings}
            icon={<CalendarOutlined />}
          >
            Refresh Calendar
          </Button>
        }
      >
        <Row gutter={24}>
          <Col span={12}>
            <div className="calendar-section">
              <h4>Select Date to View Bookings</h4>
              <Calendar
                fullscreen={false}
                value={selectedDate}
                onSelect={onCalendarSelect}
                dateCellRender={dateCellRender}
                className="booking-calendar"
              />
            </div>
          </Col>

          <Col span={12}>
            <div className="availability-section">
              <h4>
                <ClockCircleOutlined className="section-icon" />
                Bookings for {selectedDate.format('MMMM DD, YYYY')}
              </h4>
              
              {loadingBookings ? (
                <Spin tip="Loading bookings..." />
              ) : (
                <>
                  {selectedDateBookings.length > 0 ? (
                    <List
                      size="small"
                      dataSource={selectedDateBookings}
                      className="bookings-list"
                      renderItem={(booking) => (
                        <List.Item
                          className="booking-item"
                          onClick={() => onBookingClick && onBookingClick(booking)}
                          style={{ cursor: onBookingClick ? 'pointer' : 'default' }}
                        >
                          <div className="booking-card">
                            <div className="booking-header">
                              <div className="booking-type">
                                <Tag color={getBookingTagColor(booking.bookingType || booking.platformIdentity)}>
                                  {getPlatformName(booking.bookingType || booking.platformIdentity)}
                                </Tag>
                                <Tag color="default">
                                  {booking.status || 'CONFIRMED'}
                                </Tag>
                              </div>
                            </div>
                            
                            <div className="booking-details">
                              <div className="booking-title">
                                <UserOutlined className="icon" />
                                {booking.bookingType === BOOKING_TYPES.MANUAL_BOOKING || 
                                 booking.platformIdentity === BOOKING_TYPES.MANUAL_BOOKING
                                  ? <strong>{booking.title || 'Manual Reservation'}</strong>
                                  : <strong>{booking.guest || booking.clientName || 'Guest'}</strong>
                                }
                              </div>
                              
                              {booking.reservationDetails && (
                                <div className="client-info">
                                  <small>
                                    {booking.reservationDetails.firstName} {booking.reservationDetails.lastName}
                                    {booking.reservationDetails.email && ` - ${booking.reservationDetails.email}`}
                                  </small>
                                </div>
                              )}

                              <div className="booking-time">
                                <CalendarOutlined className="icon" />
                                {moment(booking.bookingStartDate, ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY'], true).format('DD MMM YYYY')}
                                {booking.bookingEndDate && 
                                 booking.bookingEndDate !== booking.bookingStartDate && (
                                  <> - {moment(booking.bookingEndDate, ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY'], true).format('DD MMM YYYY')}</>
                                )}
                              </div>

                              {booking.blocks && booking.blocks.length > 0 && (
                                <div className="time-blocks">
                                  <ClockCircleOutlined className="icon" />
                                  {booking.blocks.map((block, index) => (
                                    <Tag key={index} className="time-tag">
                                      {block.startTime} - {block.endTime}
                                    </Tag>
                                  ))}
                                </div>
                              )}

                              {booking.description && (
                                <div className="booking-description">
                                  <small>{booking.description}</small>
                                </div>
                              )}
                            </div>
                          </div>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <div className="no-data">
                      <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                      <p>No bookings for this date</p>
                    </div>
                  )}

                  {/* Legend */}
                  <div className="legend">
                    <div className="legend-title">
                      <strong>Booking Types:</strong>
                    </div>
                    <div className="legend-items">
                      <div className="legend-item">
                        <Badge status="success" />
                        <Tag color="green" size="small">Online Booking</Tag>
                        <span className="legend-note">(MillionSpaces Platform)</span>
                      </div>
                      <div className="legend-item">
                        <Badge status="processing" />
                        <Tag color="blue" size="small">Manual Reservation</Tag>
                        <span className="legend-note">(Blocked by Host)</span>
                      </div>
                      <div className="legend-item">
                        <Badge status="warning" />
                        <Tag color="orange" size="small">Offline Booking</Tag>
                        <span className="legend-note">(Admin Created)</span>
                      </div>
                      <div className="legend-item">
                        <Badge status="default" />
                        <Tag color="purple" size="small">Google Calendar</Tag>
                        <span className="legend-note">(Synced Event)</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default CalendarView;
